import type {Mode} from './Preview';
import {getPublishData} from '../../firebase_utils';
import {useCallback, useEffect, useState} from 'react';
import type {AstView} from 'rendition-core';
import {useLoadDocument} from 'Hooks/useLoadDocument';

interface Args {
    mode: Mode;
    id: string;
    view: AstView | null;
    setView: (view: AstView) => void;
    setWebflowJson: React.Dispatch<React.SetStateAction<string>>;
}

const useLoadPreviewDocument = ({mode, id, view, setView, setWebflowJson}: Args) => {
    const [sandBoxLink, setSandboxLink] = useState('');

    const {
        loadDocument,
        loading,
        setLoading,
        error: errored,
    } = useLoadDocument(setView, setSandboxLink, setWebflowJson);

    // Calls [loadDocument], unless mode is Publish and id is complex, then
    // retrieve the pageId from firestore and call [loadDocument] with that.
    const loadPreviewDocument = useCallback(async () => {
        if (mode === 'Publish') {
            const splittedId = id.split('/');
            // Differ from publish/"id" or publish/"nested/ids".
            // When using pageId, its length is 1. When using a named page, its length is > 1.
            if (splittedId.length > 1) {
                const data = (await getPublishData(splittedId.join('|'))).val();
                if (data !== undefined) {
                    const pageId = data['pageId'];
                    return await loadDocument(pageId);
                }
            } else if (splittedId.length === 1) {
                return await loadDocument(splittedId[0]);
            }
        }

        return await loadDocument(id);
    }, [id, loadDocument, mode]);

    useEffect(() => {
        loadPreviewDocument();
    }, [loadPreviewDocument]);

    return {
        loading,
        errored,
        sandBoxLink,
        setLoading,
        setSandboxLink,
        loadDocument: loadPreviewDocument,
    };
};

export default useLoadPreviewDocument;
